import React, { useState, useEffect } from "react";
import Select from "react-select";
import * as XLSX from "xlsx";
import ProductApi from "../apiProvider/product";

const StockReportLayer = () => {
    // Determine default dates
    const today = new Date();
    const tenDaysAgo = new Date(today.getTime() - (10 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
    const todayDate = today.toISOString().split('T')[0];

    // State Variables
    const [fromDate, setFromDate] = useState(tenDaysAgo);
    const [toDate, setToDate] = useState(todayDate);
    const [products, setProducts] = useState([]);

    const [selectedProduct, setSelectedProduct] = useState(null);

    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: '#3A3530',
            boxShadow: state.isFocused ? '0 0 0 1px #3A3530' : null,
            '&:hover': {
                borderColor: '#3A3530'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? '#3A3530'
                : state.isFocused
                    ? '#EEEAE5'
                    : null,
            color: state.isSelected ? 'white' : '#3A3530',
            '&:active': {
                backgroundColor: '#3A3530',
                color: 'white'
            }
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#3A3530'
        })
    };

    // Date Formatter: DD-MM-YYYY HH:mm
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return "N/A";
        return d.toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    // Initial Data Fetch
    useEffect(() => {
        fetchDropdowns();
    }, []);

    const fetchDropdowns = async () => {
        try {
            // Fetch Products
            const prodRes = await ProductApi.getAllproductList({ limit: 2000 });
            if (prodRes.status && prodRes.response) {
                let data = prodRes.response.data || prodRes.response;
                if (Array.isArray(data)) {
                    const opts = data.map(p => ({
                        value: p._id,
                        label: `${p.productName} (${p.productCode})`
                    }));
                    setProducts(opts);
                }
            }
        } catch (err) {
            console.error("Error fetching dropdowns", err);
        }
    };

    const handleSearch = async () => {
        setLoading(true);
        try {
            const params = {
                page: 0,
                limit: 2000,
                fromDate,
                toDate,
                productId: selectedProduct?.value,
            };

            const result = await ProductApi.getStockReport(params);

            if (result.status && result.response) {
                const list = result.response.data || [];
                setReportData(Array.isArray(list) ? list : []);
                setCurrentPage(1);
            } else {
                setReportData([]);
                setCurrentPage(1);
            }
        } catch (error) {
            console.error("Error fetching report", error);
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!reportData.length) return;

        const exportData = reportData.map(item => ({
            "Date": formatDate(item.date),
            "Product Name": item.productName || "N/A",
            "Product Code": item.productCode || "N/A",
            "Type": item.type || "N/A",
            "Previous Stock": item.previousStock || 0,
            "Quantity": item.quantity || 0,
            "Current Stock": item.currentStock || 0,
            "Handled By": item.handledBy || "N/A",
            "Remarks": item.remarks || "N/A"
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Stock Report");
        XLSX.writeFile(wb, `Stock_Report_${fromDate}_to_${toDate}.xlsx`);
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {/* Centralized Filter Section */}
                            <div className="row justify-content-center mb-4">
                                <div className="col-lg-6 col-md-8">
                                    <h5 className="text-center mb-4">Report Filters</h5>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">From Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label fw-bold">To Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="mb-4">
                                        <label className="form-label fw-bold">Select Product</label>
                                        <Select
                                            options={products}
                                            value={selectedProduct}
                                            onChange={setSelectedProduct}
                                            isClearable
                                            placeholder="All Products"
                                            classNamePrefix="select"
                                            styles={customStyles}
                                        />
                                    </div>

                                    {/* Search Button Block */}
                                    <div className="d-grid" style={{ marginTop: '20px', marginBottom: '20px' }}>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleSearch}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Generating Report...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bx bx-search-alt-2 me-2"></i> Search Report
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Export Action Bar - Right Aligned */}
                            {reportData.length > 0 && (
                                <div className="d-flex justify-content-end mb-3">
                                    <button
                                        className="btn btn-success d-flex align-items-center gap-2"
                                        onClick={handleExport}
                                    >
                                        <i className="bx bx-file"></i> Export Excel
                                    </button>
                                </div>
                            )}

                            {/* Results Table */}
                            <div className="table-responsive">
                                <table className="table table-bordered table-striped table-hover mb-0 align-middle" style={{ minWidth: "1500px" }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center" style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>S.No</th>
                                            <th style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>Date</th>
                                            <th style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>Product Name</th>
                                            <th style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>Code</th>
                                            <th className="text-center" style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>Type</th>
                                            <th className="text-center" style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>Prev Stock</th>
                                            <th className="text-center" style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>Quantity</th>
                                            <th className="text-center" style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>Curr Stock</th>
                                            <th style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>Handled By</th>
                                            <th style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>Remarks</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {reportData.length > 0 ? (
                                            reportData
                                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                                .map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="text-center">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                                        <td>{formatDate(item.date)}</td>
                                                        <td>{item.productName}</td>
                                                        <td>{item.productCode}</td>
                                                        <td className="text-center">
                                                            <span className={`badge ${item.type === 'order' || item.type === 'adjustment-out' ? 'bg-danger' :
                                                                item.type === 'purchase' || item.type === 'return' || item.type === 'adjustment' ? 'bg-success' : 'bg-secondary'}`}>
                                                                {item.type}
                                                            </span>
                                                        </td>
                                                        <td className="text-center">{item.previousStock || 0}</td>
                                                        <td className={`text-center fw-bold ${item.quantity < 0 ? 'text-danger' : 'text-success'}`}>
                                                            {item.quantity > 0 ? `+${item.quantity}` : item.quantity}
                                                        </td>
                                                        <td className="text-center">{item.currentStock || 0}</td>
                                                        <td>{item.handledBy}</td>
                                                        <td>{item.remarks}</td>
                                                    </tr>
                                                ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8" className="text-center py-4 text-muted">
                                                    {loading ? "Fetching data..." : "No records found matching criteria"}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {reportData.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-4">
                                    <div>
                                        Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, reportData.length)} of {reportData.length} entries
                                    </div>
                                    <nav>
                                        <ul className="pagination mb-0">
                                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link text-secondary" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>Previous</button>
                                            </li>
                                            <li className={`page-item ${currentPage === Math.ceil(reportData.length / itemsPerPage) ? 'disabled' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    style={{ backgroundColor: '#3a3530', color: '#fff', borderColor: '#3a3530' }}
                                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(reportData.length / itemsPerPage)))}>
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}

                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StockReportLayer;
